import React from "react";
import { Card, Col, Row, Tooltip } from "antd";
import {
  DeploymentUnitOutlined,
  ExperimentOutlined,
  InfoCircleOutlined,
  RobotOutlined,
  ToolOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import {
  MachinerySummaryData,
  fmtINR,
  fmtINRCompact,
} from "./machineryShared";

// KPI cards, style copied from ManpowerKpiCards — compact ₹ value with a
// Tooltip carrying the exact fmtINR amount. Total (= norm + tools + equipment
// + consumables), the Norm/Tools split (with % shares), the Equipment card
// (deployment norms — only rendered when the source has cost, so an older
// backend keeps today's four-card layout) and the consumables bucket, which
// is KPI-only: it is part of the total but never itemized in the register
// below.

const valueStyle: React.CSSProperties = {
  fontSize: 20,
  fontWeight: 700,
  color: "#1a1a2e",
  marginTop: 6,
  fontVariantNumeric: "tabular-nums",
  cursor: "default",
  width: "fit-content",
};

const titleStyle = (color: string): React.CSSProperties => ({
  fontSize: 12.5,
  fontWeight: 600,
  color,
  display: "flex",
  alignItems: "center",
  gap: 6,
});

const cardStyle = (background: string, border: string): React.CSSProperties => ({
  borderRadius: 12,
  background,
  border: `1px solid ${border}`,
  height: "100%",
});

const subStyle: React.CSSProperties = {
  fontSize: 12,
  color: "#8c8c8c",
  marginTop: 2,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const MachineryKpiCards: React.FC<{
  data?: MachinerySummaryData | null;
  grandTotal: number;
  normCost: number;
  toolsCost: number;
  equipmentCost?: number;
  consumablesCost: number;
}> = ({ grandTotal, normCost, toolsCost, equipmentCost = 0, consumablesCost }) => {
  const sharePct = (cost: number) =>
    grandTotal > 0 ? `${((cost / grandTotal) * 100).toFixed(1)}% of total` : "—";

  return (
    <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
      <Col xs={24} sm={12} xl={6}>
        <Card
          size="small"
          style={cardStyle("#f6ffed", "#b7eb8f")}
          bodyStyle={{ padding: "12px 16px" }}
        >
          <div style={titleStyle("#389e0d")}>
            <WalletOutlined /> Total Machinery Cost
          </div>
          <Tooltip title={fmtINR(grandTotal)}>
            <div style={valueStyle}>{fmtINRCompact(grandTotal)}</div>
          </Tooltip>
          <div style={subStyle}>Norms + tools + equipment + consumables</div>
        </Card>
      </Col>
      <Col xs={24} sm={12} xl={6}>
        <Card
          size="small"
          style={cardStyle("#f0f5ff", "#adc6ff")}
          bodyStyle={{ padding: "12px 16px" }}
        >
          <div style={titleStyle("#2f54eb")}>
            <DeploymentUnitOutlined /> From Norm Cost
          </div>
          <Tooltip title={fmtINR(normCost)}>
            <div style={valueStyle}>{fmtINRCompact(normCost)}</div>
          </Tooltip>
          <div style={subStyle}>{sharePct(normCost)}</div>
        </Card>
      </Col>
      <Col xs={24} sm={12} xl={6}>
        <Card
          size="small"
          style={cardStyle("#fff7e6", "#ffd591")}
          bodyStyle={{ padding: "12px 16px" }}
        >
          <div style={titleStyle("#fa8c16")}>
            <ToolOutlined /> Tools Cost
          </div>
          <Tooltip title={fmtINR(toolsCost)}>
            <div style={valueStyle}>{fmtINRCompact(toolsCost)}</div>
          </Tooltip>
          <div style={subStyle}>{sharePct(toolsCost)}</div>
        </Card>
      </Col>
      {equipmentCost > 0 ? (
        <Col xs={24} sm={12} xl={6}>
          <Card
            size="small"
            style={cardStyle("#f6ffed", "#95de64")}
            bodyStyle={{ padding: "12px 16px" }}
          >
            <div style={titleStyle("#237804")}>
              <RobotOutlined /> Equipment Cost
              <Tooltip title="Applied deployment norms — per-machine hours × hourly rates">
                <InfoCircleOutlined
                  style={{ color: "#8c8c8c", fontSize: 12, cursor: "help" }}
                />
              </Tooltip>
            </div>
            <Tooltip title={fmtINR(equipmentCost)}>
              <div style={valueStyle}>{fmtINRCompact(equipmentCost)}</div>
            </Tooltip>
            <div style={subStyle}>{sharePct(equipmentCost)}</div>
          </Card>
        </Col>
      ) : null}
      <Col xs={24} sm={12} xl={6}>
        <Card
          size="small"
          style={cardStyle("#fafafa", "#d9d9d9")}
          bodyStyle={{ padding: "12px 16px" }}
        >
          <div style={titleStyle("#595959")}>
            <ExperimentOutlined /> Consumables
            <Tooltip title="Tool split materials — included in total, not itemized">
              <InfoCircleOutlined
                style={{ color: "#8c8c8c", fontSize: 12, cursor: "help" }}
              />
            </Tooltip>
          </div>
          <Tooltip title={fmtINR(consumablesCost)}>
            <div style={valueStyle}>{fmtINRCompact(consumablesCost)}</div>
          </Tooltip>
          <div style={subStyle}>{sharePct(consumablesCost)}</div>
        </Card>
      </Col>
    </Row>
  );
};

export default MachineryKpiCards;
